import { StandardTextFieldProps, TextField } from '@material-ui/core';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import { useField } from 'formik';
import styled from 'styled-components';

const MultiSelectTextField = styled(TextField)`
  /* Inline input next to tags rather than next line on focus */
  & .MuiInputBase-input:not(:focus) {
    min-width: auto;
  }
`;

interface MultiSelectFieldProps<
  T,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
> extends Omit<
  AutocompleteProps<T, true, TDisableClearable, TFreeSolo>,
  'multiple' | 'renderInput'
> {
  TextFieldProps?: Omit<StandardTextFieldProps, 'endAdornment'>;
}

function MultiSelectField<
  T,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
>({
  TextFieldProps,
  disableClearable = true,
  ...props
}: MultiSelectFieldProps<T, TDisableClearable, TFreeSolo>) {
  return (
    <Autocomplete
      {...props}
      multiple={true}
      renderInput={(params) => (
        <MultiSelectTextField {...params} {...TextFieldProps} />
      )}
    />
  );
}

interface FormikMultiSelectFieldProps<
  T,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
> extends MultiSelectFieldProps<T, TDisableClearable, TFreeSolo> {
  name: string;
}

export function FormikMultiSelectField<
  T,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
>({
  name,
  TextFieldProps,
  ...rest
}: FormikMultiSelectFieldProps<T, TDisableClearable, TFreeSolo>) {
  const [field, { error }, { setValue }] = useField({ name });

  return (
    <MultiSelectField
      {...rest}
      {...field}
      onChange={(_, value) => {
        void setValue(value);
      }}
      TextFieldProps={{
        ...TextFieldProps,
        error: !!error,
        helperText: error || TextFieldProps?.helperText,
      }}
    />
  );
}
